import { useEffect, useRef, useState, useCallback } from "react";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { TESTIMONIALS, COMPANY } from "../../data/site";

const SCROLL_AMOUNT = 460; // ~ one card width + gap

export const Testimonials = () => {
    const trackRef = useRef(null);
    const [paused, setPaused] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    // Update active index based on scroll position
    const handleScroll = useCallback(() => {
        const el = trackRef.current;
        if (!el) return;
        const cardWidth = el.scrollWidth / TESTIMONIALS.length;
        const idx = Math.round(el.scrollLeft / cardWidth);
        setActiveIndex(Math.min(idx, TESTIMONIALS.length - 1));
    }, []);

    // Auto-advance
    useEffect(() => {
        if (paused) return;
        const id = setInterval(() => {
            const el = trackRef.current;
            if (!el) return;
            const atEnd =
                el.scrollLeft + el.clientWidth >= el.scrollWidth - 4;
            if (atEnd) {
                el.scrollTo({ left: 0, behavior: "smooth" });
            } else {
                el.scrollBy({ left: SCROLL_AMOUNT, behavior: "smooth" });
            }
        }, 5000);
        return () => clearInterval(id);
    }, [paused]);

    const scrollBy = (dir) => {
        const el = trackRef.current;
        if (!el) return;
        el.scrollBy({ left: dir * SCROLL_AMOUNT, behavior: "smooth" });
    };

    const scrollToIndex = (idx) => {
        const el = trackRef.current;
        if (!el) return;
        const cardWidth = el.scrollWidth / TESTIMONIALS.length;
        el.scrollTo({ left: cardWidth * idx, behavior: "smooth" });
    };

    return (
        <section
            id="reviews"
            data-testid="testimonials-section"
            className="py-24 md:py-32 bg-ink text-bone overflow-hidden"
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 mb-12 reveal">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
                    <div>
                        <div className="text-xs tracking-[0.22em] uppercase font-bold text-ember mb-5">
                            <span className="h-px w-10 bg-ember inline-block align-middle mr-3" />
                            Reviews
                        </div>
                        <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.05] tracking-tight">
                            <span className="text-ember">{COMPANY.fiveStarReviews}+</span> five-star
                            reviews
                            <br className="hidden md:block" /> in the last 18 months.
                        </h2>
                    </div>
                    <div className="flex items-center gap-2 text-bone/80">
                        <div className="flex">
                            {[0, 1, 2, 3, 4].map((i) => (
                                <Star
                                    key={i}
                                    size={20}
                                    className="fill-ember text-ember"
                                    strokeWidth={1.5}
                                />
                            ))}
                        </div>
                        <span className="text-sm font-semibold">5.0 avg from verified clients</span>
                    </div>
                </div>
            </div>

            <div
                className="relative"
                onMouseEnter={() => setPaused(true)}
                onMouseLeave={() => setPaused(false)}
            >
                {/* Prev button */}
                <button
                    type="button"
                    onClick={() => scrollBy(-1)}
                    aria-label="Previous review"
                    data-testid="testimonials-prev-btn"
                    className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 h-12 w-12 items-center justify-center bg-bone text-ink hover:bg-ember transition-colors btn-lift"
                >
                    <ChevronLeft size={22} strokeWidth={1.75} />
                </button>

                {/* Scrollable track */}
                <div
                    ref={trackRef}
                    onScroll={handleScroll}
                    data-testid="testimonials-track"
                    className="flex gap-6 px-6 md:px-16 pb-2 overflow-x-auto scroll-smooth snap-x snap-mandatory"
                    style={{ scrollbarWidth: "none" }}
                >
                    <style>{`[data-testid="testimonials-track"]::-webkit-scrollbar{display:none;}`}</style>
                    {TESTIMONIALS.map((t, idx) => (
                        <article
                            key={`${t.name}-${idx}`}
                            data-testid={`testimonial-card-${idx}`}
                            className="w-[320px] md:w-[420px] shrink-0 snap-start bg-bone text-ink p-8 relative"
                        >
                            <Quote
                                size={32}
                                strokeWidth={1.5}
                                className="absolute -top-4 -left-2 text-copper bg-ink p-1"
                            />
                            <div className="flex mb-4">
                                {Array.from({ length: t.rating }).map((_, i) => (
                                    <Star
                                        key={i}
                                        size={16}
                                        className="fill-ember text-ember"
                                        strokeWidth={1.5}
                                    />
                                ))}
                            </div>
                            <p className="text-base leading-relaxed">{t.text}</p>
                            <div className="mt-6 pt-4 border-t border-line">
                                <div className="font-heading font-bold">{t.name}</div>
                                <div className="text-xs uppercase tracking-wider text-muted_ink">
                                    {t.location}
                                </div>
                                {t.verified && (
                                    <div className="mt-2 inline-block text-[10px] uppercase tracking-[0.18em] font-bold text-moss border border-moss/40 px-2 py-1">
                                        ✓ {t.verified}
                                    </div>
                                )}
                            </div>
                        </article>
                    ))}
                </div>

                {/* Next button */}
                <button
                    type="button"
                    onClick={() => scrollBy(1)}
                    aria-label="Next review"
                    data-testid="testimonials-next-btn"
                    className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 h-12 w-12 items-center justify-center bg-bone text-ink hover:bg-ember transition-colors btn-lift"
                >
                    <ChevronRight size={22} strokeWidth={1.75} />
                </button>
            </div>

            {/* Mobile-friendly controls + dots */}
            <div className="max-w-7xl mx-auto px-6 md:px-12 mt-8 flex items-center justify-between gap-6">
                <div className="flex md:hidden gap-2">
                    <button
                        type="button"
                        onClick={() => scrollBy(-1)}
                        aria-label="Previous review"
                        data-testid="testimonials-prev-btn-mobile"
                        className="h-10 w-10 flex items-center justify-center bg-bone text-ink"
                    >
                        <ChevronLeft size={18} />
                    </button>
                    <button
                        type="button"
                        onClick={() => scrollBy(1)}
                        aria-label="Next review"
                        data-testid="testimonials-next-btn-mobile"
                        className="h-10 w-10 flex items-center justify-center bg-bone text-ink"
                    >
                        <ChevronRight size={18} />
                    </button>
                </div>
                <div className="flex flex-1 justify-center gap-2 flex-wrap">
                    {TESTIMONIALS.map((_, idx) => (
                        <button
                            key={idx}
                            type="button"
                            onClick={() => scrollToIndex(idx)}
                            aria-label={`Go to review ${idx + 1}`}
                            data-testid={`testimonials-dot-${idx}`}
                            className={`h-2 transition-all ${
                                idx === activeIndex
                                    ? "w-10 bg-ember"
                                    : "w-2 bg-bone/30 hover:bg-bone/60"
                            }`}
                        />
                    ))}
                </div>
                <div className="hidden md:block text-xs uppercase tracking-[0.2em] font-bold text-bone/60 min-w-[80px] text-right">
                    {activeIndex + 1} / {TESTIMONIALS.length}
                </div>
            </div>
        </section>
    );
};
